import React, { useEffect, useState } from 'react';
import FileUpload from './FileUpload';

const MENU_CATEGORIES = ['Starters', 'Main Course', 'Sides', 'Desserts', 'Drinks'];

const emptyItem = () => ({
  title: '',
  description: '',
  price: '',
  category: '',
});

const StepMenuStarter = ({ formData, updateFormData, userId = 'anonymous', errors }) => {
  const initialMenuSource = formData.menuDraft?.source || 'manual';
  const [menuSource, setMenuSource] = useState(initialMenuSource);
  const [manualItems, setManualItems] = useState(() => {
    if (
      formData.menuDraft?.source === 'manual' &&
      Array.isArray(formData.menuDraft.items) &&
      formData.menuDraft.items.length > 0
    ) {
      return formData.menuDraft.items;
    }
    return [emptyItem()];
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [previewItems, setPreviewItems] = useState(
    formData.menuDraft?.items || []
  );
  const [uploadedFileUrl, setUploadedFileUrl] = useState(
    formData.menuDraft?.fileUrl || ''
  );

  useEffect(() => {
    if (formData.menuDraft?.source === 'manual') {
      setManualItems(
        Array.isArray(formData.menuDraft.items) && formData.menuDraft.items.length
          ? formData.menuDraft.items
          : [emptyItem()]
      );
    }
  }, [formData.menuDraft]);

  useEffect(() => {
    if (Array.isArray(formData.menuDraft?.items)) {
      setPreviewItems(formData.menuDraft.items);
    }
  }, [formData.menuDraft?.items]);

  const pushMenuDraft = (draft) => {
    updateFormData({
      menuDraft: draft,
      menuStatus: draft?.items?.length ? 'draft' : formData.menuStatus,
    });
  };

  const handleSourceChange = (value) => {
    setMenuSource(value);
    if (value === 'upload') {
      pushMenuDraft({
        source: 'upload',
        fileUrl: uploadedFileUrl,
        items: [],
      });
    } else if (value === 'ai' && previewItems.length) {
      pushMenuDraft({
        source: 'ai',
        items: previewItems,
        prompt: aiPrompt,
      });
    }
  };

  const handleManualItemChange = (index, field, value) => {
    const nextItems = manualItems.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item
    );
    setManualItems(nextItems);
  };

  const addManualItem = () => {
    setManualItems((items) => [...items, emptyItem()]);
  };

  const removeManualItem = (index) => {
    setManualItems((items) => {
      if (items.length === 1) return items;
      return items.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const handleManualSave = () => {
    const validItems = manualItems
      .map((item) => ({
        ...item,
        price: item.price ? Number(item.price).toFixed(2) : '',
      }))
      .filter(
        (item) => item.title && item.price && item.category
      );

    if (!validItems.length) {
      window.alert('Add at least one menu item with a title, price, and category.');
      return;
    }

    setPreviewItems(validItems);
    pushMenuDraft({
      source: 'manual',
      items: validItems,
    });
  };

  const handleFileUpload = (fileUrl) => {
    setUploadedFileUrl(fileUrl);
    pushMenuDraft({
      source: 'upload',
      fileUrl,
      items: [],
    });
  };

  const generateAiMenu = async () => {
    if (!aiPrompt.trim()) {
      window.alert('Describe your food so we can suggest a starter menu.');
      return;
    }

    setGenerating(true);
    try {
      // Placeholder for AI generation. Replace with real integration.
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const mockItems = [
        {
          title: 'Signature Jollof Rice',
          description: 'Slow-cooked tomatoes, bell peppers, house spice blend.',
          price: '12.50',
          category: 'Main Course',
        },
        {
          title: 'Plantain Bites',
          description: 'Sweet fried plantain served with pepper sauce.',
          price: '5.00',
          category: 'Starters',
        },
        {
          title: 'Coconut Cheesecake',
          description: 'Creamy cheesecake with toasted coconut crumble.',
          price: '6.50',
          category: 'Desserts',
        },
      ];
      setPreviewItems(mockItems);
      pushMenuDraft({
        source: 'ai',
        prompt: aiPrompt,
        items: mockItems,
      });
    } finally {
      setGenerating(false);
    }
  };

  const renderMenuBuilder = () => {
    if (menuSource === 'upload') {
      return (
        <div className="space-y-4">
         <p className="text-body-sm text-text-primary-secondary">
            Upload your existing menu as a PDF, image, or document. We’ll turn it into a digital draft for you to refine later.
          </p>
          <FileUpload
            label="Upload your existing menu"
            description="Supported files: PDF, photos, documents up to 10MB."
            userId={userId || 'anonymous'}
            bucket="fbo-menus"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            maxSizeMB={10}
            onUploadComplete={handleFileUpload}
          />
          {uploadedFileUrl && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-[16px] text-body-sm text-green-700">
              Menu uploaded successfully. We’ll process it and drop the draft into your dashboard.
            </div>
          )}
        </div>
      );
    }

    if (menuSource === 'ai') {
      return (
        <div className="space-y-4">
          <label className="block text-body-md font-medium text-text-primary">
            Tell us about your food
          </label>
          <textarea
            rows={4}
            placeholder="e.g., Nigerian home cooking focused on party jollof, grilled chicken, and plantain."
            value={aiPrompt}
            onChange={(event) => setAiPrompt(event.target.value)}
            className="w-full px-4 py-3 rounded-[20px] text-text-primary bg-[#FAFAFA] border border-solid !border-[#F5F5F5] outline-none focus:!border-[#FEC51C] placeholder:text-[#A3A3A3] text-body-md"
          />
          <button
            type="button"
            className={`btn-primary ${generating ? 'opacity-75 cursor-wait' : ''}`}
            onClick={generateAiMenu}
            disabled={generating}
          >
            {generating ? 'Generating menu…' : 'Generate sample menu'}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-body-sm text-text-primary-secondary">
          Add the dishes you plan to offer. Even two or three items help us launch your shop with confidence.
        </p>
        {manualItems.map((item, index) => (
          <div
            key={index}
            className="p-4 border border-[#ECECEC] rounded-[16px] space-y-3"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
              <div>
                <label className="block text-body-sm font-medium text-text-primary mb-1">
                  Dish name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Smoky party jollof"
                  value={item.title}
                  onChange={(event) =>
                    handleManualItemChange(index, 'title', event.target.value)
                  }
                  className="w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border border-solid !border-[#F5F5F5] outline-none focus:!border-[#FEC51C] placeholder:text-[#A3A3A3] text-body-md"
                />
              </div>
              <div>
                <label className="block text-body-sm font-medium text-text-primary mb-1">
                  Price (£) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="12.50"
                  value={item.price}
                  onChange={(event) =>
                    handleManualItemChange(index, 'price', event.target.value)
                  }
                  className="w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border border-solid !border-[#F5F5F5] outline-none focus:!border-[#FEC51C] placeholder:text-[#A3A3A3] text-body-md"
                />
              </div>
              <div>
                <label className="block text-body-sm font-medium text-text-primary mb-1">
                  Category *
                </label>
                <select
                  value={item.category}
                  onChange={(event) =>
                    handleManualItemChange(index, 'category', event.target.value)
                  }
                  className="w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border border-solid !border-[#F5F5F5] outline-none focus:!border-[#FEC51C] placeholder:text-[#A3A3A3] text-body-md"
                >
                  <option value="">Select category</option>
                  {MENU_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeManualItem(index)}
                  className="btn-secondary w-full text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50"
                  disabled={manualItems.length === 1}
                >
                  Remove
                </button>
              </div>
            </div>
            <div>
              <label className="block text-body-sm font-medium text-text-primary mb-1">
                Description
              </label>
              <textarea
                rows={2}
                placeholder="Tell buyers what makes this special (allergens, spice level, portion size)."
                value={item.description}
                onChange={(event) =>
                  handleManualItemChange(index, 'description', event.target.value)
                }
                className="w-full px-3 py-3 rounded-[12px] border border-[#E5E5E5] text-body-sm focus:outline-none focus:ring-2 focus:ring-[#FEC51C]"
              />
            </div>
          </div>
        ))}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={addManualItem}
          >
            Add another dish
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleManualSave}
          >
            Save menu draft
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-body-md text-text-primary font-medium">
          How would you like to start your menu?
        </p>
        <div className="space-y-3">
          {[
            { value: 'manual', label: 'Enter dishes manually', helper: 'Perfect if you already know what you’ll serve.' },
            { value: 'upload', label: 'Upload your existing menu', helper: 'Upload a PDF, image, or document and we’ll convert it.' },
            { value: 'ai', label: 'Let Kinn draft it with AI', helper: 'Describe your food and get a starter menu instantly.' },
          ].map((option) => (
            <label
              key={option.value}
              className={`flex items-start gap-3 p-4 border rounded-[16px] cursor-pointer transition-all duration-200 ${
                menuSource === option.value
                  ? 'border-[#FEC51C] bg-[#FFF7E6]'
                  : 'border-[#ECECEC] hover:border-[#FEC51C]'
              }`}
            >
              <input
                type="radio"
                name="menuSource"
                value={option.value}
                checked={menuSource === option.value}
                onChange={(event) => handleSourceChange(event.target.value)}
                className="mt-1"
              />
              <span>
                <span className="block text-body-md font-semibold text-text-primary">
                  {option.label}
                </span>
                <span className="block text-body-sm text-text-primary-secondary">
                  {option.helper}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#F0F0F0] rounded-[20px] p-6">
        {renderMenuBuilder()}
      </div>

      {previewItems.length > 0 && (
        <div className="bg-white border border-[#F0F0F0] rounded-[20px] p-6 space-y-4">
          <h4 className="text-body-lg font-semibold text-text-primary">Menu preview</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {previewItems.map((item, index) => (
              <div key={index} className="border border-[#ECECEC] rounded-[16px] p-4 space-y-2">
                <h5 className="text-body-md font-semibold text-text-primary">
                  {item.title}
                </h5>
                <p className="text-body-sm text-text-primary-secondary">{item.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-body-xs uppercase tracking-wide text-text-primary-secondary">
                    {item.category}
                  </span>
                  <span className="text-body-md font-semibold text-text-primary">
                    £{item.price}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {errors.menu && (
        <div className="bg-red-50 border border-red-200 rounded-[16px] p-4 text-body-sm text-red-700">
          {errors.menu}
        </div>
      )}
    </div>
  );
};

export default StepMenuStarter;
